import * as React from "react";
import { SvgIcon, SvgIconProps, makeStyles, createStyles, Theme, Tooltip } from "@material-ui/core";
import { LevelThreshold } from "../../../core/models/LevelThreshold";
import { calculateLevelPercentage, getLevelColor } from "../../../helpers/installationTankHelpers";
import { getThresholdDescriptionFromKey } from "../../../helpers/thresholdLevelsHelpers";
import { levelStatus } from "../../../core/models/LevelStatus";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& .a,.b": { fill: "#fff" },
      "& .a,.c": { stroke: "#707070" },
      "& .a": { strokeWidth: 2 },
      "& .c,.e": { fill: "none" },
      "& .c": { strokeDasharray: 2 },
      "& .d,.f": { stroke: "none", fill: theme.customPaper.background.tankContainerBackground },
      "& .f": { fill: "#707070" },
    },
  })
);

type TankSVGProps = {
  value?: number;
  capacity?: number;
  thresholds?: LevelThreshold;
  showOutsideLabel?: boolean;
  status?: levelStatus;
  opaque?: boolean;
} & SvgIconProps;

function TankSVG({
  value,
  capacity,
  thresholds,
  status,
  opaque = false,
  showOutsideLabel = false,
  ...others
}: TankSVGProps) {
  const fullHeight = 210;
  const labels: JSX.Element[] = [];

  const classes = useStyles();

  const renderLevel = () => {
    if (!capacity || !value) {
      return;
    }

    const fillColorBasedOnStatus = status
      ? getLevelColor(status)
      : value
      ? getLevelColor(value, thresholds)
      : "none";

    const percentageValue = calculateLevelPercentage(value, capacity);
    const useHeight = (fullHeight * percentageValue) / 100;
    const useY = (fullHeight * (100 - percentageValue)) / 100;

    return (
      <rect
        x="1"
        y={useY}
        width="130"
        height={useHeight}
        fill={fillColorBasedOnStatus}
        opacity={opaque ? 0.4 : 1}
        stroke="none"
      />
    );
  };

  const renderThresholds = () => {
    if (!thresholds || !capacity) {
      return;
    }

    const thresholdLines = [];

    for (const threshold in thresholds) {
      const thresholdValue = thresholds[threshold as keyof LevelThreshold];

      if (!thresholdValue) {
        continue;
      }

      const percentageValue = calculateLevelPercentage(thresholdValue, capacity);

      if (percentageValue >= 100 || percentageValue <= 0) {
        continue;
      }

      const useY = (fullHeight * (100 - percentageValue)) / 100;
      const tooltipText = `${getThresholdDescriptionFromKey(
        threshold as keyof LevelThreshold
      )}: ${percentageValue}%`;

      const line = (
        <Tooltip key={`threshold_${threshold}`} title={tooltipText}>
          <g>
            <line className="c" x2="125.665" transform={`translate(0 ${useY})`} />
            {/* invisible as rect event listener */}
            <rect width="125.665" y={useY} height="2" stroke="transparent" fill="transparent" />
          </g>
        </Tooltip>
      );

      if (showOutsideLabel) {
        const label = (
          <Tooltip key={`threshold_${threshold}_label`} title={tooltipText}>
            <g>
              <rect className="a" y={useY} x="-45" height="20" width="40" rx="10" fill="#fff" />
              <text
                fontSize="16"
                y={useY + 16}
                x="-40"
                className="text"
              >{`${percentageValue}%`}</text>
            </g>
          </Tooltip>
        );
        labels.push(label);
      }

      thresholdLines.push(line);
    }

    return thresholdLines.map((x) => x);
  };

  const renderLabels = () => {
    return <g transform="translate(0, 12)">{labels.map((x) => x)}</g>;
  };

  return (
    <SvgIcon
      className={classes.root}
      viewBox={
        showOutsideLabel && (thresholds?.low || thresholds?.veryLow)
          ? "-46 0 181 291.943"
          : "0 0 127.5 280"
      }
      {...others}
    >
      <g className="j" transform="matrix(1, 0, 0, 1, -8.57, 0)">
        <g className="a" transform="translate(108 179.94)">
          <rect className="d" width="33" height="100" />
          <rect className="e" x="1" y="1" width="31" height="98" />
        </g>
      </g>
      <g className="i" transform="matrix(1, 0, 0, 1, -8.57, 0)">
        <g className="a" transform="translate(9 179.94)">
          <rect className="d" width="33" height="100" />
          <rect className="e" x="1" y="1" width="31" height="98" />
        </g>
      </g>
      <g className="h" transform="matrix(1, 0, 0, 1, -8.57, 0)">
        <g className="a" transform="translate(58 179.94)">
          <rect className="d" width="33" height="100" />
          <rect className="e" x="1" y="1" width="31" height="98" />
        </g>
      </g>
      <g className="b" transform="translate(132.146 246.953) rotate(180)">
        <path
          className="d"
          d="M 131.0113830566406 16.53276443481445 L 1.134984135627747 16.53276443481445 C 1.3063805103302 15.89381122589111 1.640474081039429 15.25028610229492 2.134253978729248 14.60996532440186 C 2.929962873458862 13.57814693450928 4.137653827667236 12.55041980743408 5.723799228668213 11.55532836914062 C 8.966435432434082 9.521001815795898 13.63805389404297 7.686164855957031 19.60885429382324 6.101783275604248 C 25.62948989868164 4.504183292388916 32.64685440063477 3.249165058135986 40.4660530090332 2.371583223342896 C 48.57500076293945 1.461474061012268 57.19047164916992 1.000001311302185 66.07318115234375 1.000001311302185 C 74.95587158203125 1.000001311302185 83.57136535644531 1.461474061012268 91.68030548095703 2.371583223342896 C 99.49948883056641 3.249165058135986 106.5168685913086 4.504183292388916 112.5374908447266 6.101783275604248 C 118.5083084106445 7.686164855957031 123.1799240112305 9.521001815795898 126.4225616455078 11.55532836914062 C 128.0087127685547 12.55041980743408 129.2164001464844 13.57814693450928 130.0121154785156 14.60996532440186 C 130.5058898925781 15.25028610229492 130.8399810791016 15.89381122589111 131.0113830566406 16.53276443481445 Z"
        />
        <path
          className="f"
          d="M 66.07318115234375 2.000000953674316 C 57.22760009765625 2.000000953674316 48.64963531494141 2.459364891052246 40.57758331298828 3.365328788757324 C 32.80714416503906 4.237456321716309 25.83854675292969 5.483310699462891 19.86534118652344 7.068328857421875 C 14.07449340820312 8.604947090148926 9.368202209472656 10.44945526123047 6.255233764648438 12.40242004394531 C 4.973731994628906 13.20639419555664 3.52978515625 14.29829597473145 2.696334838867188 15.53276443481445 L 129.4500274658203 15.53276443481445 C 128.6165771484375 14.29829597473145 127.1726379394531 13.20639419555664 125.8911285400391 12.40242004394531 C 122.7781600952148 10.44945526123047 118.0718688964844 8.604947090148926 112.2810211181641 7.068328857421875 C 106.3078155517578 5.483310699462891 99.33920288085938 4.237456321716309 91.56876373291016 3.365328788757324 C 83.4967041015625 2.459364891052246 74.91876220703125 2.000000953674316 66.07318115234375 2.000000953674316 M 66.07318115234375 1.9073486328125e-06 C 102.5643768310547 1.9073486328125e-06 132.1463623046875 7.849692344665527 132.1463623046875 17.53276443481445 C 86.40869140625 17.53276443481445 45.92453002929688 17.53276443481445 0 17.53276443481445 C 0 7.849692344665527 29.58196258544922 1.9073486328125e-06 66.07318115234375 1.9073486328125e-06 Z"
        />
      </g>
      <g className="g" transform="matrix(1, 0, 0, 1, -8.57, 0)">
        <g className="a" transform="translate(9 21.94)">
          <rect className="d" width="132" height="212" />
          <rect className="e" x="1" y="1" width="130" height={fullHeight} />
          {renderLevel()}
          {renderThresholds()}
        </g>
      </g>

      <g className="b" transform="translate(0)">
        <path
          className="d"
          d="M 131.0916137695312 24.59116554260254 L 1.054751396179199 24.59116554260254 C 1.363636374473572 21.77450180053711 2.978783130645752 19.01285552978516 5.867708206176758 16.3674201965332 C 9.103489875793457 13.40434741973877 13.76190853118896 10.73312950134277 19.71356391906738 8.427983283996582 C 25.7172908782959 6.102638244628906 32.71628952026367 4.275711059570312 40.51618194580078 2.997929096221924 C 48.60872650146484 1.672201871871948 57.20734405517578 1.000001907348633 66.07318115234375 1.000001907348633 C 74.93901824951172 1.000001907348633 83.53762054443359 1.672201871871948 91.63018035888672 2.997929096221924 C 99.4300537109375 4.275711059570312 106.429069519043 6.102638244628906 112.4328002929688 8.427983283996582 C 118.3844375610352 10.73312950134277 123.042854309082 13.40434741973877 126.2786560058594 16.3674201965332 C 129.1675720214844 19.01285552978516 130.7827301025391 21.77450180053711 131.0916137695312 24.59116554260254 Z"
        />
        <path
          className="f"
          d="M 66.07318115234375 2.000001907348633 C 57.26134490966797 2.000001907348633 48.71710968017578 2.667783737182617 40.6778564453125 3.984766006469727 C 32.94603729248047 5.251401901245117 26.01414489746094 7.060056686401367 20.07472991943359 9.360475540161133 C 14.23948669433594 11.6205472946167 9.686782836914062 14.22616577148438 6.543037414550781 17.10492897033691 C 4.229888916015625 19.2231330871582 2.791671752929688 21.39726257324219 2.247650146484375 23.59116554260254 L 129.8987121582031 23.59116554260254 C 129.3546905517578 21.39726257324219 127.9164733886719 19.2231330871582 125.6033096313477 17.10492897033691 C 122.4595794677734 14.22616577148438 117.9068756103516 11.6205472946167 112.0716400146484 9.360475540161133 C 106.1322174072266 7.060056686401367 99.20032501220703 5.251401901245117 91.468505859375 3.984766006469727 C 83.42923736572266 2.667783737182617 74.88501739501953 2.000001907348633 66.07318115234375 2.000001907348633 M 66.07318115234375 1.9073486328125e-06 C 102.5643768310547 1.9073486328125e-06 132.1463623046875 11.45754718780518 132.1463623046875 25.59116554260254 C 86.40869140625 25.59116554260254 45.92453002929688 25.59116554260254 0 25.59116554260254 C 0 11.45754718780518 29.58196258544922 1.9073486328125e-06 66.07318115234375 1.9073486328125e-06 Z"
        />
      </g>
      {showOutsideLabel && renderLabels()}
    </SvgIcon>
  );
}

export default TankSVG;
